import { TOUR_ELEMENT_POSITION_STABLE_TIMEOUT_MS } from 'constants/tour';

export const waitForElementPositionStable = async (
  selector: string,
  stableDuration = 500,
  checkInterval = 50
): Promise<void> => {
  const element = document.querySelector(selector) as HTMLElement | null;

  if (!element) {
    throw new Error(`Element with selector "${selector}" not found`);
  }

  let lastPosition: DOMRect | null = null;
  let stableStartTime: number | null = null;
  const timeout = TOUR_ELEMENT_POSITION_STABLE_TIMEOUT_MS;
  const startTime = Date.now();

  const getElementPosition = (): DOMRect | null => {
    if (!document.contains(element)) {
      return null;
    }
    return element.getBoundingClientRect();
  };

  const hasPositionChanged = (current: DOMRect, previous: DOMRect): boolean => {
    return (
      Math.abs(current.top - previous.top) > 1 ||
      Math.abs(current.left - previous.left) > 1 ||
      Math.abs(current.width - previous.width) > 1 ||
      Math.abs(current.height - previous.height) > 1
    );
  };

  while (Date.now() - startTime < timeout) {
    await new Promise((resolve) => setTimeout(resolve, checkInterval));

    const currentPosition = getElementPosition();

    if (!currentPosition) {
      throw new Error(
        `Element with selector "${selector}" was removed from DOM`
      );
    }

    if (lastPosition && hasPositionChanged(currentPosition, lastPosition)) {
      lastPosition = currentPosition;
      stableStartTime = null;
      continue;
    }

    if (!lastPosition) {
      lastPosition = currentPosition;
    }

    if (stableStartTime === null) {
      stableStartTime = Date.now();
    }

    const stableDurationElapsed = Date.now() - stableStartTime;
    if (stableDurationElapsed >= stableDuration) {
      return;
    }
  }

  throw new Error(`Timeout waiting for element position to stabilize`);
};
